import { useEffect, useRef } from 'react';


const BODY_DRAG_CLASS = 'saenggibu-file-drag-active';
const TARGET_OVER_CLASS = 'saenggibu-inline-file-drop-target--over';
const TARGET_SELECTOR = '[data-saenggibu-drop-target]';


const hasFilePayload = (event) => {
  const types = Array.from(event?.dataTransfer?.types || []);
  return types.includes('Files') || (event?.dataTransfer?.files?.length || 0) > 0;
};


const SaenggibuFileDropController = ({
  enabled = true,
  passthroughWhenDisabled = false,
  onFilesDropped,
  onDisabledDrop,
  onInvalidDrop,
}) => {
  const dragDepthRef = useRef(0);
  const activeTargetRef = useRef(null);

  useEffect(() => {
    const clearActiveTarget = () => {
      activeTargetRef.current?.classList.remove(TARGET_OVER_CLASS);
      activeTargetRef.current = null;
    };

    const resetDragState = () => {
      dragDepthRef.current = 0;
      clearActiveTarget();
      document.body.classList.remove(BODY_DRAG_CLASS);
    };

    const setActiveTarget = (target) => {
      if (activeTargetRef.current === target) return;
      clearActiveTarget();
      activeTargetRef.current = target;
      activeTargetRef.current?.classList.add(TARGET_OVER_CLASS);
    };

    const findDropTarget = (event) => {
      const eventTarget = event.target;
      return eventTarget instanceof Element
        ? eventTarget.closest(TARGET_SELECTOR)
        : null;
    };

    const handleDragEnter = (event) => {
      if (!hasFilePayload(event)) return;
      event.preventDefault();
      dragDepthRef.current += 1;
      if (enabled) {
        document.body.classList.add(BODY_DRAG_CLASS);
      }
    };

    const handleDragOver = (event) => {
      if (!hasFilePayload(event)) return;
      event.preventDefault();
      if (event.dataTransfer) {
        event.dataTransfer.dropEffect = 'copy';
      }
      if (!enabled) return;
      setActiveTarget(findDropTarget(event));
    };

    const handleDragLeave = (event) => {
      if (dragDepthRef.current > 0) {
        dragDepthRef.current -= 1;
      }
      if (dragDepthRef.current <= 0) {
        resetDragState();
      }
    };

    const handleDrop = (event) => {
      if (!hasFilePayload(event)) return;
      event.preventDefault();
      const target = findDropTarget(event);
      const files = Array.from(event.dataTransfer?.files || []);
      resetDragState();

      if (!enabled) {
        if (!passthroughWhenDisabled) {
          event.stopPropagation();
          onDisabledDrop?.();
        }
        return;
      }
      if (!target) {
        onInvalidDrop?.();
        return;
      }

      event.stopPropagation();
      const targetId = target.dataset.saenggibuDropTarget || 'primary';
      if (files.length > 0) {
        onFilesDropped?.(targetId, files);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        resetDragState();
      }
    };

    document.addEventListener('dragenter', handleDragEnter, true);
    document.addEventListener('dragover', handleDragOver, true);
    document.addEventListener('dragleave', handleDragLeave, true);
    document.addEventListener('drop', handleDrop, true);
    document.addEventListener('keydown', handleKeyDown);
    window.addEventListener('blur', resetDragState);

    return () => {
      document.removeEventListener('dragenter', handleDragEnter, true);
      document.removeEventListener('dragover', handleDragOver, true);
      document.removeEventListener('dragleave', handleDragLeave, true);
      document.removeEventListener('drop', handleDrop, true);
      document.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('blur', resetDragState);
      resetDragState();
    };
  }, [enabled, onDisabledDrop, onFilesDropped, onInvalidDrop, passthroughWhenDisabled]);

  return null;
};


export default SaenggibuFileDropController;
